import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";

export default function AdminLogin() {
  const { state, login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (params.get("expired")) {
      toast.error("Session expired. Please log in again.");
    }
  }, [params]);

  if (state === "authed") return <Navigate to="/admin/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Username and password are required");
      return;
    }
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      toast.success("Welcome back.");
      nav("/admin/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-blue-500/10 blur-[140px]" />

      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-10">
          <span className="font-display text-4xl tracking-tight leading-none text-white">
            <span className="italic">A</span>
            <span className="italic -ml-1.5">S</span>
          </span>
          <span className="text-[10px] tracking-[0.32em] uppercase text-white/60 border-l border-white/15 pl-3">
            Almonte Sites
          </span>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-10 w-10 rounded-xl flex items-center justify-center border border-white/10 bg-white/[0.03] text-blue-300">
              <Lock size={18} />
            </span>
            <div>
              <h1 className="font-display text-2xl text-white">Admin Access</h1>
              <p className="text-xs text-white/50">
                Private dashboard. Authorized access only.
              </p>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-white/55 font-medium">
                Username
              </Label>
              <Input
                data-testid="login-username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12 rounded-md focus-visible:ring-blue-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-white/55 font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  data-testid="login-password"
                  autoComplete="current-password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12 rounded-md focus-visible:ring-blue-500 pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                  aria-label={show ? "Hide password" : "Show password"}
                  data-testid="login-toggle-password"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={submitting}
              className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium shadow-[0_10px_40px_-10px_rgba(59,130,246,0.6)] disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-[11px] text-white/35">
            5 failed attempts trigger a temporary lockout.
          </p>
        </div>

        <div className="mt-8 text-center">
          <a
            href="/"
            className="text-xs text-white/40 hover:text-white transition-colors"
          >
            ← Back to public site
          </a>
        </div>
      </div>
    </main>
  );
}
